import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestfulApiService } from './restful-api.service';
import { PromotionalToolsCharityListResponse } from '../models/promotional-tools/promotional-tools.model';

export interface PromotionalToolCharity {
  uuid: string;
  name: string;
  coverPhoto: string | null;
  logo: string | null;
  givingLink: string | null;
  visionStatement: string | null;
}

export interface PromotionalToolProject {
  uuid: string;
  name: string;
  coverPhoto: string | null;
  logo: string | null;
  givingLink: string | null;
  visionStatement: string | null;
}

export interface PromotionalToolCharitiesResponse {
  response: PromotionalToolCharity[];
}

export interface PromotionalToolProjectsResponse {
  response: PromotionalToolProject[];
}

/** A project summary as nested under a charity in `charities-with-projects` — no cover/logo/vision data, just enough to donate. */
export interface PromotionalToolCharityWithProjectsProject {
  uuid: string;
  name: string;
  givingLink: string | null;
}

/**
 * A charity plus its projects, used to source the "Select Fund" dropdown for GS-only users
 * (no portal access, so `/user/get-user-info` — the normal source of fund data — returns
 * nothing for them). Deliberately narrower than `PromotionalToolCharity`: no balance/fund
 * number (this isn't a fund) and no cover/logo/vision data (not needed for fund selection).
 */
export interface PromotionalToolCharityWithProjects {
  uuid: string;
  name: string;
  givingLink: string | null;
  projects: PromotionalToolCharityWithProjectsProject[];
}

export interface PromotionalToolCharitiesWithProjectsResponse {
  response: PromotionalToolCharityWithProjects[];
}

export interface BackgroundImageUploadResponse {
  response: {
    uuid: string;
    url: string;
    filename: string;
    type: string;
    lastModified: number;
  };
}

export type MediaKitEntityType = 'CHARITY' | 'PROJECT';
export type MediaKitQrTarget = 'DETAILS_PAGE' | 'GIVE_LINK';

export interface MediaKitPayload {
  entityType: MediaKitEntityType;
  entityUuid: string;
  impactStatement: string;
  callToActionText: string;
  ctaCustomizationEnabled: boolean;
  ctaCustomText: string;
  backgroundImageUuid: string | null;
  colorGradient: string;
  colorBodyText: string;
  colorCtaText: string;
  colorCtaButton: string;
  qrCodeTarget: MediaKitQrTarget;
}

export interface MediaKitDto {
  uuid: string;
  entityType: MediaKitEntityType;
  charityUuid: string | null;
  charityName: string | null;
  charityLogoUrl: string | null;
  projectUuid: string | null;
  projectName: string | null;
  impactStatement: string;
  callToActionText: string;
  ctaCustomizationEnabled: boolean;
  ctaCustomText: string;
  backgroundImageUuid: string | null;
  backgroundImageUrl: string | null;
  colorGradient: string;
  colorBodyText: string;
  colorCtaText: string;
  colorCtaButton: string;
  qrCodeTarget: MediaKitQrTarget;
}

export interface MediaKitResponse {
  response: MediaKitDto | null;
}

export interface ShortUrlResponse {
  response: {
    shortCode: string;
    shortUrl: string;
  };
}

const MEDIA_KIT_BACKGROUND_COMPONENT_TYPE = 'PROMOTIONAL_TOOLS_MEDIA_KIT_BACKGROUND';

@Injectable({
  providedIn: 'root',
})
export class PromotionalToolsService {
  private readonly base = '/v1/promotional-tools';

  constructor(private apiService: RestfulApiService) {}

  /** List charities, optionally filtered server-side by search text. */
  getCharities(searchText = ''): Observable<PromotionalToolCharitiesResponse> {
    return this.apiService.get(`${this.base}/charities`, {
      searchText,
    }) as Observable<PromotionalToolCharitiesResponse>;
  }

  /** List projects for a given charity, optionally filtered server-side by search text. */
  getProjects(charityUuid: string, searchText = ''): Observable<PromotionalToolProjectsResponse> {
    return this.apiService.get(`${this.base}/charities/${charityUuid}/projects`, {
      searchText,
    }) as Observable<PromotionalToolProjectsResponse>;
  }

  /** List all live projects, optionally filtered server-side by search text. */
  getAllProjects(searchText = ''): Observable<PromotionalToolProjectsResponse> {
    return this.apiService.get(`${this.base}/projects`, {
      searchText,
    }) as Observable<PromotionalToolProjectsResponse>;
  }

  /**
   * List charities with their projects, each carrying just enough data (uuid/name/givingLink)
   * to populate a fund-style selector. Used as the GS-only fallback source for the Donate Now
   * tab's "Select Fund" dropdown, since `/user/get-user-info` (the normal fund source) requires
   * portal access and returns nothing for GS-only users.
   */
  getCharitiesWithProjects(): Observable<PromotionalToolCharitiesWithProjectsResponse> {
    return this.apiService.get(
      `${this.base}/charities-with-projects`
    ) as Observable<PromotionalToolCharitiesWithProjectsResponse>;
  }

  /** Upload a media kit background image for the given charity/project. */
  uploadBackgroundImage(entityUuid: string, file: File): Observable<BackgroundImageUploadResponse> {
    const formData = new FormData();
    formData.append('entityUuid', entityUuid);
    formData.append('file', file);
    formData.append('componentType', MEDIA_KIT_BACKGROUND_COMPONENT_TYPE);
    return this.apiService.post(
      '/v1/component-media/upload',
      formData
    ) as Observable<BackgroundImageUploadResponse>;
  }

  /** Delete a previously uploaded media kit background image. */
  deleteBackgroundImage(fileUuid: string): Observable<{ response: string }> {
    return this.apiService.delete('/v1/component-media/delete', {
      fileUuid,
      componentType: MEDIA_KIT_BACKGROUND_COMPONENT_TYPE,
    }) as Observable<{ response: string }>;
  }

  /** Persist the media kit for the selected charity/project. */
  saveMediaKit(payload: MediaKitPayload): Observable<MediaKitResponse> {
    return this.apiService.post(`${this.base}/media-kit`, payload) as Observable<MediaKitResponse>;
  }

  /** Retrieve the saved media kit. */
  getMediaKit(): Observable<MediaKitResponse> {
    return this.apiService.get(`${this.base}/media-kit`) as Observable<MediaKitResponse>;
  }

  /** Shorten a long URL so the generated QR code stays low-density. */
  shortenUrl(originalUrl: string): Observable<ShortUrlResponse> {
    return this.apiService.post('/v1/short-url', { originalUrl }) as Observable<ShortUrlResponse>;
  }
}
